import {useEffect, useMemo, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {Alert, Button, Col, Empty, Menu, Modal, Row, Spin, Tag, Tooltip, Typography} from "antd";
import {CheckCircleOutlined, CheckOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined, UndoOutlined} from "@ant-design/icons";
import {t} from "i18next";
import PermissionGroup from "./PermissionGroup";
import {useModal} from "../../core/context/ModalContext";
import RoleCreateEdit from "./RoleCreateEdit";
import ApiService from "../../core/service/api-service";
import {PERMISSIONS, useUser} from "../../core/context/UserContext";

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'tr')

export default function RoleManager()
{
    const {openModal, closeModal} = useModal()
    const {isSuperAdmin} = useUser()
    const history = useHistory()

    const [roles, setRoles] = useState([])
    const [rolesLoaded, setRolesLoaded] = useState(false)
    const [role, setRole] = useState(null)
    const [roleLoading, setRoleLoading] = useState(false)

    // savedPermissions: sunucudaki hâl. selectedPermissions: ekranda düzenlenen hâl. Aradaki fark
    // "kaydedilmemiş değişiklik"tir; kaydet/geri al düğmeleri ve uyarılar buna bakar.
    const [savedPermissions, setSavedPermissions] = useState([])
    const [selectedPermissions, setSelectedPermissions] = useState([])
    const [saving, setSaving] = useState(false)
    const [lastSavedAt, setLastSavedAt] = useState(null)

    // Rol listesinde hızlı gezinirken geç dönen eski cevap yeni seçilen rolün üstüne yazmasın.
    const loadSeq = useRef(0)

    // Süper yönetici rolü alt seviyeden düzenlenemez: adı da izinleri de kilitlidir. Sunucu zaten
    // reddediyor; burada da kapatıp kullanıcıyı boşuna uğraştırmıyoruz.
    const roleLocked = !isSuperAdmin() && (role?.permissionIdList || []).includes(PERMISSIONS.SUPER_ADMIN)

    const diff = useMemo(() => {
        const saved = new Set(savedPermissions)
        const selected = new Set(selectedPermissions)

        return {
            added: [...selected].filter(id => !saved.has(id)).length,
            removed: [...saved].filter(id => !selected.has(id)).length,
        }
    }, [savedPermissions, selectedPermissions])

    const dirty = diff.added + diff.removed > 0

    const loadRoles = async () => {
        const response = await ApiService.Role.GetList()
        setRolesLoaded(true)

        if(!response.success)
            return []

        const list = [...response.data].sort(byName)
        setRoles(list)
        return list
    }

    const loadRole = async (roleId) => {
        const seq = ++loadSeq.current
        setRoleLoading(true)

        const response = await ApiService.Role.Get(roleId)

        if(seq !== loadSeq.current)
            return false

        setRoleLoading(false)

        if(!response.success)
            return false

        const ids = [...new Set(response.data.permissionIdList || [])]
        setRole(response.data)
        setSavedPermissions(ids)
        setSelectedPermissions(ids)
        setLastSavedAt(null)
        return true
    }

    // Kaydedilmemiş değişiklik varken başka yere geçmeden önce sorulur.
    const confirmDiscard = (proceed) => {
        if(!dirty)
        {
            proceed()
            return
        }

        Modal.confirm({
            title: t('Unsaved changes'),
            icon: <ExclamationCircleOutlined />,
            content: t('UNSAVED_PERMISSIONS_CONFIRM'),
            okText: t('Leave without saving'),
            okButtonProps: {danger: true},
            cancelText: t('Cancel'),
            onOk: proceed,
        })
    }

    const save = async () => {
        if(!role?.id || roleLocked || !dirty || saving)
            return

        setSaving(true)
        try
        {
            const response = await ApiService.Role.SetRolePermissions(role.id, selectedPermissions)
            if(response.success)
            {
                // Sunucunun kaydettiği hâli yeniden okuyoruz: ekranda "kaydedildi" görünen şey
                // gerçekten veritabanındaki olsun. Bu arada başka role geçildiyse işaret oraya düşmesin.
                if(await loadRole(role.id))
                    setLastSavedAt(new Date())
            }
        }
        finally
        {
            setSaving(false)
        }
    }

    const discard = () => setSelectedPermissions(savedPermissions)

    const selectRole = (roleId) => {
        if(roleId === role?.id)
            return

        confirmDiscard(() => loadRole(roleId))
    }

    const createRole = () => {
        confirmDiscard(() => {
            const before = new Set(roles.map(r => r.id))

            openModal(t('New Role'), (
                <RoleCreateEdit
                    onSaved={async () => {
                        closeModal()

                        // Create id dönmüyor; yeni rol listede önceden olmayan kayıttır.
                        const list = await loadRoles()
                        const created = list.filter(r => !before.has(r.id)).sort((a, b) => b.id - a.id)[0]
                        if(created)
                            loadRole(created.id)
                    }}
                />
            ))
        })
    }

    // Ad değişikliği izinlere dokunmaz; bekleyen izin değişiklikleri korunur.
    const renameRole = () => {
        openModal(t('Edit Role Name'), (
            <RoleCreateEdit
                roleId={role.id}
                roleName={roles.find(r => r.id === role.id)?.name}
                onSaved={async () => {
                    closeModal()
                    await loadRoles()
                }}
            />
        ))
    }

    // İlk açılışta listedeki ilk rol seçili gelir.
    useEffect(() => {
        (async () => {
            const list = await loadRoles()
            if(list.length > 0)
                loadRole(list[0].id)
        })()
    }, [])

    // Uygulama içi gezinme (menü, geri tuşu): kaydedilmemiş değişiklik varsa önce sor.
    useEffect(() => {
        if(!dirty)
            return

        const unblock = history.block((location) => {
            Modal.confirm({
                title: t('Unsaved changes'),
                icon: <ExclamationCircleOutlined />,
                content: t('UNSAVED_PERMISSIONS_CONFIRM'),
                okText: t('Leave without saving'),
                okButtonProps: {danger: true},
                cancelText: t('Cancel'),
                onOk: () => {
                    unblock()
                    history.push(location)
                },
            })
            return false
        })

        return unblock
    }, [dirty, history])

    // Sekmeyi kapatma / yenileme: tarayıcının kendi uyarısı.
    useEffect(() => {
        if(!dirty)
            return

        const handler = (e) => {
            e.preventDefault()
            e.returnValue = ''
        }

        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [dirty])

    // Ctrl+S / Cmd+S ile kaydet. Dinleyici bir kez bağlanır, güncel save ref'ten okunur.
    const saveRef = useRef(save)
    saveRef.current = save

    useEffect(() => {
        const handler = (e) => {
            if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's')
            {
                e.preventDefault()
                saveRef.current()
            }
        }

        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    const roleName = roles.find(r => r.id === role?.id)?.name ?? role?.name

    const renderStatus = () => {
        if(dirty)
        {
            return (
                <Tag color={'warning'} icon={<ExclamationCircleOutlined />} style={{marginInlineEnd: 0, padding: '2px 8px'}}>
                    {t('Unsaved changes')}
                    {diff.added > 0 && <b style={{marginInlineStart: 6, color: '#389e0d'}}>+{diff.added}</b>}
                    {diff.removed > 0 && <b style={{marginInlineStart: 6, color: '#cf1322'}}>−{diff.removed}</b>}
                </Tag>
            )
        }

        if(lastSavedAt)
        {
            return (
                <Tag color={'success'} icon={<CheckCircleOutlined />} style={{marginInlineEnd: 0, padding: '2px 8px'}}>
                    {t('Saved')} · {lastSavedAt.toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}
                </Tag>
            )
        }

        return (
            <Typography.Text type={'secondary'}>
                <CheckOutlined /> {t('No unsaved changes')}
            </Typography.Text>
        )
    }

    return (
        <Row gutter={[16, 16]}>
            <Col xs={24} md={7} lg={6} xl={5}>
                <Button block icon={<PlusOutlined />} onClick={createRole} style={{marginBottom: 8}}>
                    {t('New Role')}
                </Button>

                {rolesLoaded && roles.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Menu
                        mode={'inline'}
                        selectedKeys={role?.id ? [String(role.id)] : []}
                        items={roles.map(r => ({key: String(r.id), label: r.name}))}
                        onClick={({key}) => selectRole(Number(key))}
                        style={{borderInlineEnd: 'none'}}
                    />
                )}
            </Col>

            <Col xs={24} md={17} lg={18} xl={19}>
                {role && (
                    <Spin spinning={roleLoading}>
                        {/* Kaydetme çubuğu sayfa kaydırılsa da görünür kalır; değişiklik varken rengi değişir. */}
                        <div
                            style={{
                                position: 'sticky',
                                top: 64,
                                zIndex: 3,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                marginBottom: 12,
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: `1px solid ${dirty ? '#ffe58f' : '#f0f0f0'}`,
                                background: dirty ? '#fffbe6' : '#fff',
                                transition: 'background-color .2s, border-color .2s',
                            }}
                        >
                            <div style={{display: 'flex', alignItems: 'center', gap: 4, minWidth: 0}}>
                                <Typography.Title level={5} style={{margin: 0}} ellipsis>
                                    {roleName}
                                </Typography.Title>
                                <Tooltip title={t('Edit Role Name')}>
                                    <Button type={'text'} icon={<EditOutlined />} disabled={roleLocked} onClick={renameRole} />
                                </Tooltip>
                            </div>

                            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8}}>
                                {renderStatus()}
                                <Button icon={<UndoOutlined />} disabled={!dirty || saving} onClick={discard}>
                                    {t('Discard changes')}
                                </Button>
                                <Tooltip title={'Ctrl+S'}>
                                    <Button
                                        type={'primary'}
                                        icon={<SaveOutlined />}
                                        loading={saving}
                                        disabled={!dirty || roleLocked}
                                        onClick={save}
                                    >
                                        {t('Set Permissions')}
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>

                        {roleLocked && (
                            <Alert
                                type={'warning'}
                                showIcon
                                style={{marginBottom: 16}}
                                message={t('You are not allowed to edit this role')}
                            />
                        )}

                        <PermissionGroup
                            readOnly={roleLocked}
                            savedPermissions={savedPermissions}
                            selectedPermissions={selectedPermissions}
                            onChangeSelectedPermissions={setSelectedPermissions}
                        />
                    </Spin>
                )}
            </Col>
        </Row>
    )
}
